import { Component, Input, ViewChild, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgApexchartsModule, ChartComponent } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexTooltip,
  ApexStroke,
  ApexGrid,
  ApexYAxis,
  ApexFill,
  ApexTheme
} from 'ng-apexcharts';

export interface ChartData {
  period: string;
  value: number;
}

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  stroke: ApexStroke;
  tooltip: ApexTooltip;
  fill: ApexFill;
  theme: ApexTheme;
};

@Component({
  selector: 'app-statistics-chart',
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  template: `
    <div class="bg-white rounded-xl border border-gray-200 p-6">
      <div class="flex items-center justify-between mb-6">
        <h3 class="text-lg font-semibold text-gray-900 font-outfit">{{ title }}</h3>
        <div class="flex items-center space-x-2">
          @for (period of periods; track period.value) {
            <button 
              class="px-3 py-1 text-sm rounded-md transition-colors"
              [class]="selectedPeriod === period.value ? 'bg-brand-50 text-brand-500' : 'text-gray-500 hover:bg-gray-50'"
              (click)="selectPeriod(period.value)">
              {{ period.label }}
            </button>
          }
        </div>
      </div>
      
      <!-- ApexCharts Chart -->
      <div class="h-80">
        <apx-chart
          [series]="chartOptions.series"
          [chart]="chartOptions.chart"
          [xaxis]="chartOptions.xaxis"
          [yaxis]="chartOptions.yaxis"
          [dataLabels]="chartOptions.dataLabels"
          [grid]="chartOptions.grid"
          [stroke]="chartOptions.stroke"
          [tooltip]="chartOptions.tooltip"
          [fill]="chartOptions.fill"
          [theme]="chartOptions.theme">
        </apx-chart>
      </div>
    </div>
  `,
  styles: ``
})
export class StatisticsChartComponent implements OnInit, OnChanges {
  @ViewChild("chart") chart!: ChartComponent;
  @Input() title: string = 'Statistics';
  @Input() chartData: ChartData[] = [];
  
  selectedPeriod: string = 'week';
  
  periods = [
    { label: 'Week', value: 'week' },
    { label: 'Month', value: 'month' },
    { label: 'Year', value: 'year' }
  ];

  public chartOptions: ChartOptions = {
    series: [
      {
        name: "Revenue",
        data: [],
        color: '#b55299'
      },
      {
        name: "Target",
        data: [],
        color: '#e8cde4'
      }
    ],
    chart: {
      height: 320,
      type: "area",
      toolbar: {
        show: false
      },
      background: 'transparent'
    },
    dataLabels: {
      enabled: false
    },
    stroke: {
      curve: "smooth",
      width: 3
    },
    xaxis: {
      categories: [],
      labels: {
        style: {
          colors: '#667085',
          fontSize: '12px',
          fontFamily: 'Raleway, sans-serif'
        }
      },
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: '#667085',
          fontSize: '12px',
          fontFamily: 'Raleway, sans-serif'
        },
        formatter: function (val: number) {
          return "$" + (val / 1000).toFixed(0) + "K";
        }
      }
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return "$" + val.toLocaleString();
        }
      },
      theme: 'light'
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.3,
        opacityTo: 0.1,
        stops: [0, 90, 100]
      }
    },
    grid: {
      borderColor: '#f2f4f7',
      strokeDashArray: 3
    },
    theme: {
      mode: 'light'
    }
  };

  ngOnInit() {
    this.updateChartData();
  }

  ngOnChanges() {
    this.updateChartData();
  }

  selectPeriod(period: string): void {
    this.selectedPeriod = period;
    this.updateChartData();
  }

  private updateChartData(): void {
    if (this.chartData.length === 0) return;

    const categories = this.chartData.map(d => this.getShortPeriod(d.period));
    const revenueData = this.chartData.map(d => d.value);
    const targetData = this.chartData.map(d => Math.round(d.value * 1.15)); // 15% higher than actual

    this.chartOptions = {
      ...this.chartOptions,
      series: [
        {
          name: "Revenue",
          data: revenueData,
          color: '#b55299'
        },
        {
          name: "Target",
          data: targetData,
          color: '#e8cde4'
        }
      ],
      xaxis: {
        ...this.chartOptions.xaxis,
        categories: categories
      }
    };
  }

  private getShortPeriod(period: string): string {
    if (period.includes('Jan')) return 'Jan';
    if (period.includes('Feb')) return 'Feb';
    if (period.includes('Mar')) return 'Mar';
    if (period.includes('Apr')) return 'Apr';
    if (period.includes('May')) return 'May';
    if (period.includes('Jun')) return 'Jun';
    if (period.includes('Jul')) return 'Jul';
    if (period.includes('Aug')) return 'Aug';
    if (period.includes('Sep')) return 'Sep';
    if (period.includes('Oct')) return 'Oct';
    if (period.includes('Nov')) return 'Nov';
    if (period.includes('Dec')) return 'Dec';
    if (period.includes('Mon')) return 'Mon';
    if (period.includes('Tue')) return 'Tue';
    if (period.includes('Wed')) return 'Wed';
    if (period.includes('Thu')) return 'Thu';
    if (period.includes('Fri')) return 'Fri';
    if (period.includes('Sat')) return 'Sat';
    if (period.includes('Sun')) return 'Sun';
    return period.slice(0, 3);
  }
}